import { useEffect, useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Trash2 } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import { CountBadge } from '@modules/_shared/components/CountBadge';
import { DropZoneButton } from '@modules/_shared/components/DropZone';
import { FileDeleteDialog } from '@modules/_shared/components/FileDeleteDialog';
import { FileIcon } from '@modules/_shared/components/FileIcon';
import { FileViewerDialog } from '@modules/_shared/components/FileViewerDialog';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { useFileUpload } from '@modules/_shared/utils/files/file-upload';
import type { LeadDetailsDrawer } from '@modules/lead-details/type';
import { useLocale } from '@shared/hooks/locale';

type SchemaFile = NonNullable<
  LeadDetailsDrawer['property']
>['property_files'][number]['file'];

const FilesItem = ({
  file,
  onDelete,
}: {
  file: SchemaFile;
  onDelete: (fileId: string) => void;
}) => {
  const hasViewer = file.mimetype === 'application/pdf';
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [viewerDialog, setViewerDialog] = useState(false);

  return (
    <>
      <FileDeleteDialog
        fileId={file.id}
        open={deleteDialog}
        onDelete={onDelete}
        onClose={() => setDeleteDialog(false)}
      />
      {hasViewer && (
        <FileViewerDialog
          url={file.url}
          name={file.name}
          open={viewerDialog}
          onClose={() => setViewerDialog(false)}
        />
      )}
      <Box
        onClick={() => {
          if (hasViewer) {
            setViewerDialog(true);
          } else {
            window.open(file.url, '_blank', 'noopener,noreferrer');
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1,
          cursor: 'pointer',
          borderRadius: '8px',
          mx: 1,
          transition: 'background-color 0.15s ease',
          '&:hover': {
            bgcolor: 'grey.50',
            '& .file-delete-btn': { opacity: 1 },
          },
        }}
      >
        <FileIcon mimetype={file.mimetype} filename={file.name} />
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            noWrap
            sx={{ fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.4 }}
          >
            {file.name}
          </Typography>
          <Typography
            noWrap
            sx={{
              color: 'text.secondary',
              fontSize: '0.75rem',
              lineHeight: 1.4,
            }}
          >
            <TimeAgo dateString={file.created_at} />
            {file.uploader && (
              <span>
                {' · '}
                {`${file.uploader.first_name ?? ''} ${
                  file.uploader.last_name ?? ''
                }`.trim()}
              </span>
            )}
          </Typography>
        </Stack>
        <IconButton
          className="file-delete-btn"
          onClick={e => {
            e.stopPropagation();
            setDeleteDialog(true);
          }}
          size="small"
          sx={{
            opacity: 0,
            color: 'text.secondary',
            transition: 'all 0.15s ease',
            '&:hover': { color: 'error.main' },
          }}
        >
          <Trash2 size={16} />
        </IconButton>
      </Box>
    </>
  );
};

const PendingFilesItem = ({ file }: { file: File }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1,
        mx: 1,
        opacity: 0.6,
      }}
    >
      <FileIcon mimetype={file.type} filename={file.name} />
      <Typography
        noWrap
        sx={{ flex: 1, fontSize: '0.875rem', fontWeight: 500 }}
      >
        {file.name}
      </Typography>
      <CircularProgress size={18} disableShrink color="inherit" />
    </Box>
  );
};

const CREATE_FILE_WITH_PROPERTY_LINK = gql(/* GraphQL */ `
  mutation CreateFileWithPropertyLink($object: property_files_insert_input!) {
    insert_property_files_one(object: $object) {
      property_id
      file_id
      file {
        id
        name
        url
        mimetype
        created_at
        uploader {
          first_name
          last_name
        }
      }
    }
  }
`);

type FilesListProps = {
  lead: LeadDetailsDrawer;
};

export const LeadDetailsFilesCard = ({ lead }: FilesListProps) => {
  const { t } = useLocale();
  const [uploadFile] = useFileUpload('file');
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);

  const [createFileWithPropertyLinkMutation] = useMutation(
    CREATE_FILE_WITH_PROPERTY_LINK,
    { onError: setErrorLike },
  );

  const property = lead.property;
  const propertyFiles = property?.property_files;

  const propertyFileItems = useMemo<SchemaFile[]>(() => {
    if (!propertyFiles) {
      return [];
    }

    return propertyFiles.map(propertyFile => propertyFile.file);
  }, [propertyFiles]);

  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [files, setFiles] = useState<SchemaFile[]>(propertyFileItems);

  useEffect(() => {
    setFiles(propertyFileItems);
  }, [propertyFileItems]);

  const handleRemoveFile = (fileId: string) => {
    setFiles(prev => prev.filter(file => file.id !== fileId));
  };

  if (!property) {
    return null;
  }

  const addPendingFile = (file: File) =>
    setPendingFiles(prev => [...prev, file]);
  const removePendingFile = (file: File) =>
    setPendingFiles(prev => prev.filter(item => item !== file));

  return (
    <>
      <ListingAccordion
        title={
          <>
            {t('files')}
            {files.length > 0 && <CountBadge count={files.length} />}
          </>
        }
        disablePadding
      >
        <Stack sx={{ py: 0.5 }}>
          {files.length === 0 && pendingFiles.length === 0 && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                textAlign: 'center',
                py: 2,
              }}
            >
              {t('noFiles')}
            </Typography>
          )}
          {files.map(file => (
            <FilesItem key={file.id} file={file} onDelete={handleRemoveFile} />
          ))}
          {pendingFiles.map((file, index) => (
            <PendingFilesItem key={index} file={file} />
          ))}
        </Stack>
        <Divider />
        <DropZoneButton
          onDrop={files => {
            files.forEach(file => {
              addPendingFile(file);
              uploadFile(file, async params => {
                if (params.data != null) {
                  try {
                    const fileResult = await createFileWithPropertyLinkMutation(
                      {
                        variables: {
                          object: {
                            property_id: property.id,
                            file: {
                              data: {
                                url: params.data.url,
                                name: file.name,
                                mimetype: file.type,
                                size: file.size,
                              },
                            },
                          },
                        },
                      },
                    );

                    const createdFile =
                      fileResult.data?.insert_property_files_one?.file;
                    if (createdFile) {
                      setFiles(prev => [
                        createdFile,
                        ...prev.filter(
                          existingFile => existingFile.id !== createdFile.id,
                        ),
                      ]);
                    }

                    removePendingFile(file);
                  } catch (error) {
                    removePendingFile(file);
                  }
                } else {
                  removePendingFile(file);
                }
              });
            });
          }}
        />
      </ListingAccordion>
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </>
  );
};
